/**
 * ListHeader Component
 *
 * A reusable header row for list/table views. Only visible on desktop.
 * Provides consistent styling for column headers.
 *
 * @example
 * ```tsx
 * <ListHeader
 *   columns={[
 *     { key: 'date', label: 'Date', width: 'w-28' },
 *     { key: 'name', label: 'Name', width: 'flex-1' },
 *     { key: 'status', label: 'Status', width: 'w-24', align: 'center' },
 *   ]}
 *   showActions={true}
 *   actionsLabel="Actions"
 * />
 * ```
 */
import React from 'react';

export type ColumnAlignment = 'left' | 'center' | 'right';

export interface ListHeaderColumn {
  /** Unique identifier for the column */
  key: string;
  /** Column header label */
  label: string;
  /** CSS width class (e.g., 'w-28', 'flex-1 min-w-0') */
  width?: string;
  /** Text alignment */
  align?: ColumnAlignment;
  /** Whether column is sortable (future feature) */
  sortable?: boolean;
  /** Sort direction if currently sorted (future feature) */
  sortDirection?: 'asc' | 'desc' | null;
  /** Callback when column header is clicked for sorting (future feature) */
  onSort?: () => void;
}

export interface ListHeaderProps {
  /** Array of column definitions */
  columns: ListHeaderColumn[];
  /** Whether to show an actions column */
  showActions?: boolean;
  /** Label for the actions column */
  actionsLabel?: string;
  /** Width class for the actions column */
  actionsWidth?: string;
  /** Optional CSS class for the container */
  className?: string;
}

const getAlignmentClass = (align: ColumnAlignment = 'left'): string => {
  const alignments: Record<ColumnAlignment, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };
  return alignments[align];
};

const ListHeader: React.FC<ListHeaderProps> = ({
  columns,
  showActions = false,
  actionsLabel = 'Actions',
  actionsWidth = 'w-20',
  className = '',
}) => {
  return (
    <div
      className={`hidden md:block bg-gray-50 px-4 py-3 rounded-lg border border-gray-200 ${className}`}
    >
      <div className="flex items-center justify-between text-sm font-semibold text-gray-700">
        {columns.map((column) => (
          <div
            key={column.key}
            className={`${column.width || 'flex-1 min-w-0'} ${getAlignmentClass(column.align)}`}
          >
            {column.sortable ? (
              <button
                onClick={column.onSort}
                className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors"
              >
                {column.label}
                {column.sortDirection && (
                  <span className="text-gray-400">
                    {column.sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            ) : (
              column.label
            )}
          </div>
        ))}
        {showActions && (
          <div className={`${actionsWidth} text-center`}>{actionsLabel}</div>
        )}
      </div>
    </div>
  );
};

export default ListHeader;
